/* The system itself, as a figure, in six views: the Inbox (the inquiry arrives), Opportunities (one deal moves to order),
   one Opportunity, the AI Workspace, Quotes and the Dashboard. The same components as the app – sidebar, top bar, inbox
   panes, pipeline board, workspace, overview – with the «Studio Bergen» case for NRK running through them.
   Auto-plays; a click on a tab takes over. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Inbox as InboxIcon, Kanban, LayoutGrid, Plus, Sparkles, Target } from "lucide-react";
import { StaticAuthProvider } from "@/context/AuthContext";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { PageHeader, Segmented, SearchField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { OppCard } from "@/components/pipeline/OppCard";
import { StageColumn, StageEmpty } from "@/components/pipeline/StageColumn";
import { ThreadList } from "@/components/inbox/ThreadList";
import { Conversation } from "@/components/inbox/Conversation";
import { ContextPanel } from "@/components/inbox/ContextPanel";
import { OverviewBody } from "@/pages/OverviewPage";
import { QuotesBody } from "@/pages/QuotesPage";
import { DEFAULT_STAGES } from "@/lib/stages";
import { fmtCompact } from "@/lib/format";
import { Toast, MbOpportunity, SCRIPT } from "./MbApp";
import { Inert, Stage } from "./Stage";
import { useCountUp } from "./motion";
import { WorkspaceStill } from "./WorkspaceMockup";
import { DEMO_USER, PIPELINE, FLOW, studioBergen, USERS, INBOX_SUMMARY, inboxThreads, inboxDetail, overviewData, QUOTES } from "./mockData";

const noop = () => {};
const resolved = async () => ({});

/* ------------------------------------------------------------------ the script */

const INBOX_BEATS = [
  { ms: 5200, toast: ["New inquiry triaged by AI", "NRK · Anne Hansen · 7 requirements, 2 open questions · priority high"] },
  { ms: 5200, toast: ["Opportunity ‘Studio Bergen’ created", "Requirements from the AI brief added – nothing retyped"] },
  { ms: 6200, toast: ["Reply drafted in Anne's language", "Concise tone · verify dates before sending"] },
];

const PIPELINE_BEATS = FLOW.map((f, i) => ({ ms: i === FLOW.length - 1 ? 5600 : 3600, toast: f.toast }));

/* the opportunity page: the same script as the hero replica – overview · requirements · solution · calculation · quote · won */
const OPPORTUNITY_BEATS = SCRIPT.map((b) => ({ ms: b.ms, toast: b.toast || null }));

/* the workspace: the pasted e-mail, then four stills of the real surface – understand · design · calculate · sent */
const WORKSPACE_BEATS = [
  { ms: 3400, at: -1, toast: null },
  { ms: 4600, at: 0, toast: ["7 requirements and 2 open questions extracted", "From Anne's e-mail – nothing retyped"] },
  { ms: 4800, at: 1, toast: ["Solution ‘Recommended’ designed from the catalogue", "4 × AW-UE160, ATEM 2 M/E, QL1, EW-DX · GM recalculated"] },
  { ms: 4200, at: 2, toast: null },
  { ms: 5400, at: 4, toast: ["Proposal v1 sent to anne.hansen@nrk.no", "Anne can read, download and accept online"] },
];

const QUOTES_BEATS = [
  { ms: 5200, toast: ["Quote v2 accepted by Anne Hansen", "NRK · Studio Bergen · accepted online, 4 views"] },
  { ms: 5600, toast: ["3 quotes waiting more than 7 days", "Aker, Trondheim Spektrum, DreamHack – follow-ups drafted"] },
];

const OVERVIEW_BEATS = [
  { ms: 5400, toast: null },
  { ms: 6400, toast: ["Aker opened quote v2 again – third time", "Suggested: a short call beats a fourth e-mail · talking points ready"] },
];

const VIEWS = [
  { key: "inbox", url: "os.mediability.no/inbox", path: "/inbox", Icon: InboxIcon, beats: INBOX_BEATS },
  { key: "pipeline", url: "os.mediability.no/pipeline", path: "/pipeline", Icon: Kanban, beats: PIPELINE_BEATS },
  { key: "opportunity", url: "os.mediability.no/opportunities/studio-bergen", path: "/pipeline", Icon: Target, beats: OPPORTUNITY_BEATS },
  { key: "workspace", url: "os.mediability.no/workspace", urlAt: "os.mediability.no/workspace/studio-bergen", path: "/workspace", Icon: Sparkles, beats: WORKSPACE_BEATS },
  { key: "quotes", url: "os.mediability.no/quotes", path: "/quotes", Icon: FileText, beats: QUOTES_BEATS },
  { key: "overview", url: "os.mediability.no/overview", path: "/overview", Icon: LayoutGrid, beats: OVERVIEW_BEATS },
];

const MANUAL_MS = 30000;

/* advance one beat at a time while in view; when a view ends, move on (or loop it while the viewer has chosen it) */
const useScript = (active) => {
  const [st, setSt] = useState({ v: 0, b: 0, loop: 0 });
  const [paused, setPaused] = useState(false);
  const manualUntil = useRef(0);
  useEffect(() => {
    if (!active || paused) return undefined;
    const t = setTimeout(() => {
      setSt((s) => {
        const beats = VIEWS[s.v].beats;
        if (s.b + 1 < beats.length) return { ...s, b: s.b + 1 };
        if (manualUntil.current > Date.now()) return { v: s.v, b: 0, loop: s.loop + 1 };
        return { v: (s.v + 1) % VIEWS.length, b: 0, loop: s.loop + 1 };
      });
    }, VIEWS[st.v].beats[st.b].ms);
    return () => clearTimeout(t);
  }, [st, active, paused]);
  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, b: 0, loop: s.loop + 1 }));
  }, []);
  return { ...st, paused, setPaused, select };
};

/* ------------------------------------------------------------------ view 1 · the inbox */

const InboxView = ({ beat, mode }) => {
  const nar = mode === "narrow";
  const full = mode === "full";
  const threads = useMemo(() => inboxThreads(beat), [beat]);
  const detail = useMemo(() => inboxDetail(beat), [beat]);
  const ref = useRef(null);

  // beat 2: the suggested reply goes into the composer – the same button a user would press
  useEffect(() => {
    if (beat < 2) return undefined;
    const t = setTimeout(() => {
      const root = ref.current;
      if (!root) return;
      const had = root.hasAttribute("inert");
      if (had) root.removeAttribute("inert");
      root.querySelector('[data-testid="inbox-ai-use-draft"]')?.click();
      if (had) root.setAttribute("inert", "");
    }, 350);
    return () => clearTimeout(t);
  }, [beat]);

  useEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);

  return (
    <div ref={ref} className="pointer-events-none flex h-full min-h-0 w-full select-none" aria-hidden="true" data-testid="mock-inbox" data-beat={beat}>
      {!nar && (
        <ThreadList
          className={full ? "w-[372px] shrink-0 border-r border-black/[0.06] bg-[hsl(var(--sidebar)/0.6)]" : "w-[340px] shrink-0 border-r border-black/[0.06] bg-[hsl(var(--sidebar)/0.6)]"}
          threads={threads}
          loading={false}
          error={null}
          onRetry={noop}
          activeId="th_nrk_studio_bergen"
          onSelect={noop}
          view="focus"
          onView={noop}
          channel={null}
          onChannel={noop}
          q=""
          onQ={noop}
          summary={INBOX_SUMMARY}
        />
      )}
      <div className="flex min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <Conversation
            data={detail}
            loading={false}
            error={null}
            onRetry={noop}
            users={USERS}
            onPatch={noop}
            onSend={resolved}
            onDraft={resolved}
            onCreateOpportunity={noop}
            onStartWorkspace={noop}
            onBack={nar ? noop : null}
            onToggleContext={noop}
            contextOpen={full}
            busy={false}
          />
        </div>
        {full && <ContextPanel data={detail} users={USERS} onCreateOpportunity={noop} busy={false} className="w-[320px] shrink-0 border-l border-black/[0.06]" />}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ view 2 · the pipeline */

const OWNER_ITEMS = [
  { key: "all", label: "Everyone" },
  { key: "mine", label: "Mine" },
  { key: "u_erik", label: "Erik" },
  { key: "u_maria", label: "Maria" },
  { key: "u_jonas", label: "Jonas" },
];

const Board = ({ beat, mode }) => {
  const nar = mode === "narrow";
  const hero = useMemo(() => studioBergen(beat), [beat]);
  const f = FLOW[beat];
  const stages = DEFAULT_STAGES.filter((s) => s.key !== "lost" && (f.won || s.key !== "won"));
  const all = useMemo(() => [hero, ...PIPELINE], [hero]);
  const open = all.filter((o) => o.status === "open");
  const totalOpen = open.reduce((s, o) => s + (o.value || 0), 0);
  const weighted = open.reduce((s, o) => s + ((o.value || 0) * (DEFAULT_STAGES.find((x) => x.key === o.stage)?.probability || 0)) / 100, 0);
  const boardRef = useRef(null);

  // pan the board so the moving opportunity stays in view
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const col = el.querySelector(`[data-testid="pipeline-stage-column-${hero.stage}"]`);
    if (!col) return;
    const target = Math.max(0, Math.min(col.offsetLeft - (nar ? 16 : 40), el.scrollWidth - el.clientWidth));
    el.scrollTo({ left: target, behavior: "smooth" });
  }, [hero.stage, nar]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="mock-pipeline" data-beat={beat}>
      <div className={nar ? "px-4 pt-5" : "px-8 pt-7 lg:px-10"}>
        <PageHeader
          eyebrow="Sales"
          title="Opportunities"
          className="mb-4"
          description={
            <span className="inline-flex flex-wrap items-center gap-x-2">
              <span><span className="font-semibold text-foreground tabular" data-num="">{open.length}</span> open</span>
              <span className="text-border">·</span>
              <span><span className="font-semibold text-foreground tabular" data-num="">{fmtCompact(totalOpen)}</span> in pipeline</span>
              <span className="text-border">·</span>
              <span><span className="font-semibold text-foreground tabular" data-num="">{fmtCompact(weighted)}</span> weighted</span>
            </span>
          }
          actions={
            nar ? null : (
              <div className="flex items-center gap-2">
                <Button variant="outline" className="gap-2" tabIndex={-1}><Plus className="h-4 w-4" /> Opportunity</Button>
                <Button className="gap-2" tabIndex={-1}><Plus className="h-4 w-4" /> New inquiry</Button>
              </div>
            )
          }
        />
        {!nar && (
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Segmented items={OWNER_ITEMS} value="all" onChange={noop} testIdPrefix="mock-pipeline-owner" />
              <Button variant="ghost" className="h-10 rounded-xl text-[13px]">{f.won ? "Hide" : "Show"} Won / Lost</Button>
            </div>
            <SearchField value="" onChange={noop} placeholder="Filter by customer or name" className="w-[260px]" />
          </div>
        )}
      </div>
      <div ref={boardRef} className={`mb-noscroll min-h-0 flex-1 overflow-x-auto overflow-y-hidden pb-8 ${nar ? "px-4" : "px-8 lg:px-10"}`}>
        <div className="flex h-full items-start gap-3.5" data-testid="mock-pipeline-board">
          {stages.map((s) => {
            const items = all.filter((o) => o.stage === s.key);
            const sum = items.reduce((a, o) => a + (o.value || 0), 0);
            return (
              <StageColumn key={s.key} stage={s} count={items.length} sum={sum} focused={s.key === hero.stage} bodyClassName="overflow-y-hidden">
                {items.map((o) => (
                  <div key={o.id} className={o.id === hero.id ? "mb-fade" : ""} data-hero={o.id === hero.id ? "1" : undefined}>
                    <OppCard o={o} draggable={false} className={o.id === hero.id ? "border-primary/40 shadow-float" : ""} />
                  </div>
                ))}
                {items.length === 0 && <StageEmpty label={s.label} />}
              </StageColumn>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ view 3 · one opportunity */

const OpportunityView = ({ beat, mode }) => {
  const b = SCRIPT[beat] || SCRIPT[0];
  return <MbOpportunity beat={{ ...b, toast: null }} tab={b.tab} mode={mode} />;
};

/* ------------------------------------------------------------------ view 4 · the quotes */

const QuotesView = ({ beat }) => {
  const ref = useRef(null);
  // beat 1: down to the list
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (beat >= 1) {
      const list = el.querySelector('[data-testid="quotes-list"]');
      if (list) el.scrollTo({ top: Math.max(0, list.offsetTop - 96), behavior: "smooth" });
    } else el.scrollTo({ top: 0, behavior: "smooth" });
  }, [beat]);
  return (
    <div ref={ref} className="mb-noscroll relative h-full min-h-0 overflow-y-auto overflow-x-hidden" data-testid="mock-quotes" data-beat={beat}>
      <QuotesBody quotes={QUOTES} error={null} onRetry={noop} />
    </div>
  );
};

/* ------------------------------------------------------------------ view 5 · the overview */

const OverviewView = ({ beat }) => {
  const data = useMemo(() => overviewData(), []);
  const ref = useRef(null);
  // beat 1: scroll down to what needs attention
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (beat >= 1) {
      const sec = el.querySelector('[data-testid="overview-attention-list"]');
      if (sec) el.scrollTo({ top: Math.max(0, sec.offsetTop - 132), behavior: "smooth" });
    } else el.scrollTo({ top: 0, behavior: "smooth" });
  }, [beat]);
  return (
    <div ref={ref} className="mb-noscroll relative h-full min-h-0 overflow-y-auto overflow-x-hidden" data-testid="mock-overview" data-beat={beat}>
      <OverviewBody data={data} error={null} onRetry={noop} firstName="David" onOpen={noop} />
    </div>
  );
};

/* ------------------------------------------------------------------ the app around them */

const App = ({ view, beat, mode, active }) => {
  const v = VIEWS[view];
  const b = v.beats[beat] || v.beats[0];
  const toast = b?.toast;
  /* the workspace itself (once started) is a page of its own in the app – no sidebar, no top bar */
  const bare = v.key === "workspace" && b.at >= 0;
  /* entry motion: the numbers in the view that just came in count up (once per mount of that view) */
  const root = useRef(null);
  useCountUp(root, active, { delay: 500 }, [view, beat]);
  return (
    <div ref={root} className="mb mb-cine flex h-full w-full overflow-hidden bg-background text-foreground" data-testid="mock-system" data-view={v.key} data-beat={beat} data-mode={mode}>
      {mode === "full" && !bare && (
        <Inert className="h-full shrink-0">
          <Sidebar activePath={v.path} className="h-full" />
        </Inert>
      )}
      <div className="relative flex min-w-0 flex-1 flex-col">
        {!bare && (
          <Inert>
            <Topbar user={DEMO_USER} />
          </Inert>
        )}
        <div key={v.key + (bare ? "-w" : "")} className="mb-view-enter min-h-0 flex-1">
          {v.key === "inbox" ? (
            <InboxView beat={beat} mode={mode} />
          ) : v.key === "pipeline" ? (
            <Inert className="h-full">
              <Board beat={beat} mode={mode} />
            </Inert>
          ) : v.key === "opportunity" ? (
            <Inert className="h-full">
              <OpportunityView beat={beat} mode={mode} />
            </Inert>
          ) : v.key === "workspace" ? (
            <Inert className="h-full">
              <WorkspaceStill at={b.at} mode={mode} />
            </Inert>
          ) : v.key === "quotes" ? (
            <Inert className="h-full">
              <QuotesView beat={beat} mode={mode} />
            </Inert>
          ) : (
            <Inert className="h-full">
              <OverviewView beat={beat} mode={mode} />
            </Inert>
          )}
        </div>
        {toast && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[104px] h-0">
            <Toast key={`${v.key}-${beat}`} t={toast} nar={mode === "narrow"} />
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ the figure: tabs · frame · caption */

const Tabs = ({ views, current, loop, paused, onSelect }) => {
  const refs = useRef([]);
  const strip = useRef(null);
  /* on a phone the switcher is a strip: keep the chosen view in the middle of it (no-op when nothing overflows) */
  useEffect(() => {
    const el = refs.current[current];
    const s = strip.current;
    if (!el || !s || s.scrollWidth <= s.clientWidth + 1) return;
    const left = el.offsetLeft - (s.clientWidth - el.offsetWidth) / 2;
    s.scrollTo({ left: Math.max(0, Math.min(left, s.scrollWidth - s.clientWidth)), behavior: "smooth" });
  }, [current]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : -1) + VIEWS.length) % VIEWS.length;
    onSelect(n);
    refs.current[n]?.focus();
  };
  return (
    <div ref={strip} className="kvl-switch" role="tablist" aria-label="View" data-paused={paused ? "true" : "false"} data-testid="tilbod-system-tabs">
      {VIEWS.map((v, i) => {
        const meta = views[i] || {};
        const on = i === current;
        const dur = v.beats.reduce((s, b) => s + b.ms, 0);
        return (
          <button
            key={v.key}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className="kvl-switch-tab"
            onClick={() => onSelect(i)}
            onKeyDown={(e) => onKey(e, i)}
            data-testid={`tilbod-system-tab-${v.key}`}
          >
            <span className="kvl-switch-ic">
              <v.Icon aria-hidden="true" />
            </span>
            <span className="kvl-switch-txt">
              <b>{meta.label || v.key}</b>
              {meta.hint && <small>{meta.hint}</small>}
            </span>
            {on && <i key={`${v.key}-${loop}`} className="kvl-switch-bar" style={{ "--dur": `${dur}ms` }} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
};

export const SystemMockup = ({ d }) => {
  const [active, setActive] = useState(false);
  const { v, b, loop, paused, setPaused, select } = useScript(active);
  const views = d?.idea?.views || [];
  const meta = views[v] || {};
  return (
    <figure className="kvl-figure kvl-figure-mock" data-testid="tilbod-idea-figure" data-view={VIEWS[v].key}>
      <Tabs views={views} current={v} loop={loop} paused={paused || !active} onSelect={select} />
      <div className="kvl-frame" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <Stage url={VIEWS[v].urlAt && (VIEWS[v].beats[b]?.at ?? -1) >= 0 ? VIEWS[v].urlAt : VIEWS[v].url} caption={d?.ui?.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1500 }} testId="tilbod-system-mockup" attrs={{ "data-view": VIEWS[v].key, "data-beat": b }} onActive={setActive}>
          {({ mode, active: on }) => (
            <StaticAuthProvider user={DEMO_USER}>
              <App view={v} beat={b} mode={mode} active={on} />
            </StaticAuthProvider>
          )}
        </Stage>
      </div>
      <figcaption key={VIEWS[v].key} className="kvl-cap-enter" data-testid="tilbod-idea-caption">
        {meta.caption || d?.idea?.caption}
      </figcaption>
    </figure>
  );
};
